import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Bot, Send, Sparkles, Loader2, RefreshCw, MessagesSquare } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { toast } from '../hooks/use-toast';

const SUGGESTIONS = [
  '¿Cómo creo una orden de compra?',
  '¿Cuántas OTs activas hay?',
  '¿Cómo apruebo una requisición?',
  'Resume las OCs sin VB de hoy',
];

export function AIConsultant() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Iter 293.i (Carlos, feb-2026): botón flotante arrastrable con persistencia.
  // Iter 307.c (Renzo, jun-2026): inicia al pie del sidebar, a la DERECHA del
  // bloque de usuario (sin tapar "Modo Oscuro" ni el nombre). Sigue siendo
  // arrastrable a gusto del usuario y su posición se recuerda (clave -v2 para
  // que todos partan en la nueva posición una vez).
  const BTN_SIZE = 64;
  const DEFAULT_POS = { left: 202, bottom: 12 };
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem('ai-consultant-pos-v2');
      if (raw) return { ...DEFAULT_POS, ...JSON.parse(raw) };
    } catch { /* noop */ }
    return DEFAULT_POS;
  });
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, startLeft: 0, startBottom: 0 });

  const clampPos = useCallback((p) => {
    const maxLeft = Math.max(0, (window.innerWidth || 1024) - BTN_SIZE);
    const maxBottom = Math.max(0, (window.innerHeight || 768) - BTN_SIZE);
    return {
      left: Math.min(Math.max(0, p.left), maxLeft),
      bottom: Math.min(Math.max(0, p.bottom), maxBottom),
    };
  }, []);

  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampPos]);

  const onPointerDown = (e) => {
    // No arrastrar con click derecho ni desde el interior si el usuario ya está usando teclado
    if (e.button !== 0) return;
    dragRef.current = {
      active: true, moved: false,
      startX: e.clientX, startY: e.clientY,
      startLeft: pos.left, startBottom: pos.bottom,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) d.moved = true;
    if (!d.moved) return;
    setPos(clampPos({
      left: d.startLeft + dx,
      bottom: d.startBottom - dy,
    }));
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    dragRef.current = { ...d, active: false };
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    if (d.moved) {
      try {
        localStorage.setItem('ai-consultant-pos-v2', JSON.stringify(pos));
      } catch { /* noop */ }
    } else {
      // Click puro (sin drag): abre el sheet
      setOpen(true);
    }
  };

  // Iniciar sesión nueva la primera vez que se abre
  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    try {
      const res = await api.post('/consultor-ia/nueva-sesion');
      const sid = res.data?.session_id;
      setSessionId(sid);
      return sid;
    } catch {
      return null;
    }
  }, [sessionId]);

  useEffect(() => {
    if (open) {
      ensureSession();
      setTimeout(() => textareaRef.current?.focus(), 250);
    }
  }, [open, ensureSession]);

  // Scroll al final cuando agregamos mensajes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, loading]);

  const enviar = async (texto) => {
    const pregunta = (texto ?? input).trim();
    if (!pregunta || loading) return;
    const sid = await ensureSession();
    setMessages(prev => [...prev, { role: 'user', content: pregunta }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/consultor-ia/preguntar', { pregunta, session_id: sid });
      const data = res.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.respuesta || '(sin respuesta)',
        meta: data.control || {},
      }]);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error consultando IA';
      setMessages(prev => [...prev, { role: 'system', content: `⚠️ ${msg}` }]);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  const nuevaConversacion = async () => {
    setMessages([]);
    setSessionId(null);
    await ensureSession();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  // No mostrar el botón si el usuario no está logueado
  if (!user) return null;

  return (
    <>
      {/* Botón flotante arrastrable (Iter 293.i) */}
      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ left: `${pos.left}px`, bottom: `${pos.bottom}px`, touchAction: 'none' }}
        className="fixed z-50 h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-2xl hover:scale-110 transition-transform flex items-center justify-center group cursor-grab active:cursor-grabbing select-none"
        title="Asistente IA (arrástrame para reubicar)"
        aria-label="Abrir asistente IA"
        data-testid="btn-asistente-ia-floating"
      >
        <Bot className="h-6 w-6 sm:h-7 sm:w-7 pointer-events-none" strokeWidth={2} />
        <span className="absolute -top-1 -right-1 bg-amber-400 rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold text-emerald-900 shadow-md pointer-events-none">
          <Sparkles className="h-3 w-3" />
        </span>
        <span className="hidden group-hover:block absolute left-full ml-3 bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
          Asistente IA
        </span>
      </button>

      {/* Sheet del chat */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="p-0 w-full sm:max-w-md flex flex-col" data-testid="sheet-asistente-ia">
          <SheetHeader className="px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-950/40 dark:to-background">
            <SheetTitle className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center">
                <Bot className="h-4 w-4" />
              </div>
              <span>Asistente CHIRINO</span>
              <Badge variant="outline" className="text-[10px] ml-auto">Beta</Badge>
              <button
                onClick={nuevaConversacion}
                className="ml-1 p-1 hover:bg-muted rounded"
                title="Nueva conversación"
                data-testid="btn-nueva-conversacion"
              >
                <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </SheetTitle>
            <p className="text-[11px] text-muted-foreground mt-1 text-left">
              Te ayudo con dudas del ERP y consultas de datos. Hablame en español.
            </p>
          </SheetHeader>

          {/* Mensajes */}
          <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef} data-testid="scroll-mensajes">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-8 space-y-4">
                <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center">
                  <MessagesSquare className="h-7 w-7 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-foreground">¿En qué te ayudo?</p>
                  <p className="text-xs mt-1">Probá una de estas preguntas:</p>
                </div>
                <div className="space-y-1.5 w-full">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => enviar(s)}
                      className="w-full text-xs text-left px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition"
                      data-testid={`suggestion-${s.slice(0, 20)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m, i) => (
                  <MessageBubble key={i} msg={m} />
                ))}
                {loading && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="typing-indicator">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Pensando…
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="border-t p-2 bg-background">
            <div className="flex gap-2 items-end">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Escribí tu consulta…"
                className="resize-none min-h-[40px] max-h-32 text-sm"
                rows={1}
                disabled={loading}
                data-testid="input-pregunta"
              />
              <Button
                size="icon"
                onClick={() => enviar()}
                disabled={loading || !input.trim()}
                className="h-10 w-10 shrink-0 bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-enviar-pregunta"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 px-1">
              Enter envía · Shift+Enter salto de línea · Las consultas quedan auditadas
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function MessageBubble({ msg }) {
  if (msg.role === 'system') {
    return (
      <div className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-2 border border-amber-200">
        {msg.content}
      </div>
    );
  }
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
          isUser
            ? 'bg-emerald-600 text-white rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm'
        }`}
        data-testid={`bubble-${msg.role}`}
      >
        {msg.content}
        {!isUser && msg.meta?.modelo && (
          <p className="text-[9px] opacity-60 mt-1">
            {msg.meta.tipo_pregunta} · {msg.meta.modelo.replace('claude-', '').replace('-20251001', '').replace('-20250929', '')} · ${msg.meta.costo_estimado_usd?.toFixed(4)}
          </p>
        )}
      </div>
    </div>
  );
}
